import React, { forwardRef, type Ref } from 'react';

import { cssMap } from '@atlaskit/css';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';

import { BadgeContainer } from '../BadgeContainer';
import { IconButton } from '../IconButton';
import { type HelpProps } from './types';

const iconSpacingStyles = cssMap({
	space050: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
});

const HELP_NOTIFICATION_BADGE_ID = 'atlassian-navigation-help-notification-count';

/**
 * __Help__
 *
 * A help button that can be passed into `AtlassianNavigation`'s `renderHelp` prop.
 *
 * - [Examples](https://atlassian.design/components/atlassian-navigation/examples#help)
 * - [Code](https://atlassian.design/components/atlassian-navigation/code)
 *
 * @deprecated `@atlaskit/atlassian-navigation` is deprecated. Use `@atlaskit/navigation-system` instead.
 */
export const Help: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<HelpProps> & React.RefAttributes<any>
> = forwardRef((props: HelpProps, ref: Ref<any>) => {
	const {
		badge,
		component,
		href,
		id,
		isDisabled,
		isSelected,
		label,
		onBlur,
		onClick,
		onFocus,
		onMouseDown,
		onMouseEnter,
		onMouseLeave,
		onMouseUp,
		target,
		testId,
		tooltip,
		...rest
	} = props;
	const button = (
		<IconButton
			component={component}
			href={href}
			icon={
				<Flex xcss={iconSpacingStyles.space050}>
					<QuestionCircleIcon
						label={typeof tooltip === 'string' ? tooltip : 'Help Icon'}
						color="currentColor"
					/>
				</Flex>
			}
			id={id}
			isDisabled={isDisabled}
			isSelected={isSelected}
			label={label}
			onBlur={onBlur}
			onClick={onClick}
			onFocus={onFocus}
			onMouseDown={onMouseDown}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onMouseUp={onMouseUp}
			ref={ref}
			target={target}
			testId={testId}
			tooltip={tooltip}
			isTooltipAnnouncementDisabled
			// These are all explicit, leaving it in just in case
			{...rest}
		/>
	);

	return badge ? (
		<BadgeContainer id={HELP_NOTIFICATION_BADGE_ID} badge={badge} role="listitem">
			{button}
		</BadgeContainer>
	) : (
		<div role="listitem">{button}</div>
	);
});
